import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CourseCard from '../components/CourseCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const MyCourses = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Filter courses when search term changes
  useEffect(() => {
    if (!user?.enrolled_courses) return;

    let filtered = user.enrolled_courses;

    // Filter by search term
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCourses(filtered);
  }, [searchTerm, user]);

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <LoadingSpinner message="Loading your courses..." />
        <Footer />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  const enrolledCourses = user?.enrolled_courses || [];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="container mx-auto max-w-7xl px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">My Courses</h1>
          <p className="text-xl text-blue-100">
            Your enrolled courses - {enrolledCourses.length} {enrolledCourses.length === 1 ? 'course' : 'courses'}
          </p>
        </div>
      </section>

      {/* Search Section */}
      {enrolledCourses.length > 0 && (
        <section className="bg-white py-8 shadow-md">
          <div className="container mx-auto max-w-7xl px-4">
            <div className="flex flex-col items-center justify-center gap-4">
              {/* Search Bar */}
              <div className="w-full max-w-4xl">
                <div className="relative rounded-full">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && e.target.blur()}
                    placeholder="Search your courses..."
                    className="py-6 lg:py-8 pl-8 pr-20 text-lg w-full text-black rounded-full focus:outline-none shadow-lg"
                    autoComplete="off"
                  />
                  <button
                    className="bg-blue-600 p-5 rounded-full absolute right-2 top-2 hover:bg-blue-700 transition-colors"
                    onClick={() => {}}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" className="text-white text-4xl inline-block" width="1em" height="1em" viewBox="0 0 24 24">
                      <g fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="11.5" cy="11.5" r="9.5"></circle>
                        <path strokeLinecap="round" d="M18.5 18.5L22 22"></path>
                      </g>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Courses Grid */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          {enrolledCourses.length === 0 ? (
            // Empty State
            <div className="text-center py-16">
              <div className="bg-white rounded-lg shadow-lg p-12 max-w-2xl mx-auto">
                <svg className="w-24 h-24 mx-auto text-gray-400 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">No Courses Yet</h2>
                <p className="text-gray-600 mb-8">
                  You haven't enrolled in any courses yet. Browse our course catalog to get started!
                </p>
                <button
                  onClick={() => navigate('/courses')}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Browse Courses
                </button>
              </div>
            </div>
          ) : filteredCourses.length === 0 ? (
            // No Search Results
            <div className="text-center py-16">
              <div className="bg-white rounded-lg shadow-lg p-12 max-w-2xl mx-auto">
                <svg className="w-24 h-24 mx-auto text-gray-400 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">No Courses Found</h2>
                <p className="text-gray-600 mb-6">
                  No courses match your search "{searchTerm}"
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Clear Search
                </button>
              </div>
            </div>
          ) : (
            // Course Grid
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MyCourses;
